import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmationModalProps } from '~/Components';
import { useDateFns } from '~/Hooks';
import { IStaff } from '~/Models/DTO/Staff.model';

import { useRequestDeactivateStaff, useRequestDeleteStaff, useRequestTrainers } from '~/requests';
import { useRequestEvents } from '~/requests/eventService';

export const useStaffDeactivationDeletion = ({
  staff,
  isInDeletionMode,
  setConfirmationModalProps,
}: {
  staff: IStaff;
  isInDeletionMode: boolean;
  setConfirmationModalProps: React.Dispatch<React.SetStateAction<ConfirmationModalProps>>;
}) => {
  // state
  const [isLoading, setIsLoading] = useState(true);
  const [isDisabled, setDisabled] = useState(true);
  const [selectedTrainerId, setSelectedTrainerId] = useState<string>('');

  //hooks
  const { t } = useTranslation();
  const { convertToProviderTimezone } = useDateFns();
  const {
    isLoading: areEventsLoading,
    error: dataError,
    data: events,
    isFetched: areEventsFetched,
  } = useRequestEvents({
    trainerId: [staff.id],
    archived: false,
    from: convertToProviderTimezone(new Date().toString()).toISOString(),
  });

  const {
    data: trainerList,
    error: trainerListError,
    isLoading: isTrainerListLoading,
    isFetched: isTrainerListFetched,
  } = useRequestTrainers({ archived: false, suspended: false });

  const {
    mutate: deactivateStaff,
    isLoading: isDeactivating,
    error: updateError,
    isSuccess,
  } = useRequestDeactivateStaff();

  const {
    mutate: deleteStaff,
    isLoading: isDeleting,
    error: deleteError,
    isSuccess: isDeleteSuccess,
  } = useRequestDeleteStaff();

  useEffect(() => {
    if (isSuccess || isDeleteSuccess) {
      onCancel();
    }
  }, [isSuccess, isDeleteSuccess]);

  const onCancel = () => {
    setDisabled(true);
    setSelectedTrainerId('');
    setConfirmationModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const hasEvents = !!events?.length && events?.length > 0;

  useEffect(() => {
    setIsLoading(!areEventsFetched || (hasEvents && !isTrainerListFetched));
    setDisabled(
      !areEventsFetched || (hasEvents && (!isTrainerListFetched || selectedTrainerId === '')),
    );
  }, [areEventsFetched, hasEvents, isTrainerListFetched, selectedTrainerId]);

  useEffect(() => {
    setConfirmationModalProps((prev) => ({
      ...prev,
      destructive: true,
      cancel: { title: t('app.cancel'), action: onCancel },
      confirm: {
        ...prev.confirm,
        disabled: isDisabled,
        isLoading: isLoading || isDeactivating || isDeleting,
        action: () => {
          if (isInDeletionMode) {
            deleteStaff({ staffId: staff.id });
          } else {
            deactivateStaff({ staffId: staff.id, substituteId: selectedTrainerId });
          }
        },
      },
    }));
  }, [isDisabled, isLoading, selectedTrainerId, isDeactivating, isDeleting]);

  return {
    selectedTrainerId,
    setSelectedTrainerId,
    trainerList,
    hasEvents,
    areEventsLoading,
    isTrainerListLoading,
    events,
    error: dataError || trainerListError || updateError || deleteError,
  };
};
